/**
 * Read path: discover cross-reference links from Word OOXML.
 *
 * How we get this: in `word-service`, the same selection is read in one sync batch as
 * `getHtml()` + `getOoxml()`. HTML usually has only the visible label ("Table 8–2");
 * OOXML still has the field instruction that names the bookmark (`REF _Ref…`).
 * This module scrapes those pairs so `injectCrossReferenceLinkFields` can put them
 * back into the HTML the LLM sees.
 *
 * OOXML shape for a complex REF field (simplified):
 *   fldChar begin → instrText ("REF _Ref48291037 \h …") → fldChar separate
 *   → text runs (`w:t`) with the display label → fldChar end
 * Word sometimes stores the same thing as one `fldSimple` with `instr="REF …"`
 * and nested text inside. We handle both.
 *
 * We ignore anchor bookmarks at headings (no field around them, so they never reach
 * `crossReferenceFromInstruction`). Only inline REF / HYPERLINK \l cross-references are collected —
 * a REF cross-reference is a link only if its instruction carries \h; HYPERLINK is always a link.
 */

/** Cross-reference: inline REF or HYPERLINK field targeting a `_Ref*` bookmark, plus visible text. */
export type CrossReferenceLink = {
  bookmarkId: string
  displayText: string
  /** Raw switches from the field instruction (e.g. `\h \* MERGEFORMAT`); empty string means a non-link cross-reference. */
  switches?: string
}

const NS = '(?:\\w+:)?'
const INSTR_TEXT_REGEX = new RegExp(
  `<${NS}instrText(?:\\s[^>]*)?>([\\s\\S]*?)<\\/${NS}instrText>`,
  'gi'
)
const FLD_SIMPLE_REGEX = new RegExp(
  `<${NS}fldSimple\\s+([^>]*)>([\\s\\S]*?)<\\/${NS}fldSimple>`,
  'gi'
)
const TEXT_NODE_REGEX = new RegExp(`<${NS}t(?:\\s[^>]*)?>([^<]*)<\\/${NS}t>`, 'g')
const FLD_CHAR_SEPARATE_OR_END_REGEX = new RegExp(
  `<${NS}fldChar[^>]*(?:\\w+:)?fldCharType="(?:separate|end)"`,
  'i'
)
const FLD_CHAR_END_REGEX = new RegExp(`<${NS}fldChar[^>]*(?:\\w+:)?fldCharType="end"`, 'i')

/** Turn XML entities (`&amp;`, etc.) back into normal characters. */
function decodeXmlText(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
}

/** Concatenate all `w:t` text nodes inside an OOXML fragment. */
function textFromFragment(fragment: string): string {
  const texts: string[] = []
  let match: RegExpExecArray | null
  TEXT_NODE_REGEX.lastIndex = 0
  while ((match = TEXT_NODE_REGEX.exec(fragment)) !== null) {
    texts.push(decodeXmlText(match[1]))
  }
  return texts.join('')
}

/**
 * Pull `_Ref…` + field switches from a REF or HYPERLINK \\l field instruction; null if not a cross-ref.
 * HYPERLINK is always a link, so its switches are fixed; a REF's switches (possibly none) are
 * carried through verbatim so a non-link REF doesn't turn into a link on reinsert.
 */
function crossReferenceFromInstruction(
  instruction: string
): { bookmarkId: string; switches: string } | null {
  const normalized = instruction.replace(/\s+/g, ' ').trim()
  const refMatch = /REF\s+(_Ref\w+)\s*(.*)/i.exec(normalized)
  if (refMatch) return { bookmarkId: refMatch[1], switches: refMatch[2].trim() }
  const hyperlinkMatch = /HYPERLINK\s+\\l\s+"(_Ref\w+)"/i.exec(normalized)
  return hyperlinkMatch ? { bookmarkId: hyperlinkMatch[1], switches: '\\h \\* MERGEFORMAT' } : null
}

/**
 * Word may split one field instruction across consecutive instrText nodes — merge when the
 * gap has no separate/end marker or display text.
 */
function mergedInstrTextBlocks(ooxml: string): { instruction: string; afterIndex: number }[] {
  const blocks: { instruction: string; afterIndex: number; startIndex: number }[] = []
  let match: RegExpExecArray | null
  INSTR_TEXT_REGEX.lastIndex = 0
  while ((match = INSTR_TEXT_REGEX.exec(ooxml)) !== null) {
    blocks.push({
      instruction: decodeXmlText(match[1]),
      afterIndex: match.index + match[0].length,
      startIndex: match.index,
    })
  }

  const merged: { instruction: string; afterIndex: number }[] = []
  for (let i = 0; i < blocks.length;) {
    let { instruction, afterIndex } = blocks[i]
    let j = i + 1
    while (j < blocks.length) {
      const gap = ooxml.slice(afterIndex, blocks[j].startIndex)
      if (FLD_CHAR_SEPARATE_OR_END_REGEX.test(gap)) break
      if (new RegExp(`<${NS}t(?:\\s|>)`, 'i').test(gap)) break
      instruction += blocks[j].instruction
      afterIndex = blocks[j].afterIndex
      j++
    }
    merged.push({ instruction, afterIndex })
    i = j
  }
  return merged
}

/** Visible label after the instruction (text between fldChar separate and end). */
function displayTextAfterInstruction(ooxml: string, afterInstructionIndex: number): string {
  const slice = ooxml.slice(afterInstructionIndex)
  const separateMatch = slice.match(
    new RegExp(`<${NS}fldChar[^>]*(?:\\w+:)?fldCharType="separate"`, 'i')
  )
  const fieldSlice = slice.slice(separateMatch ? separateMatch.index! + separateMatch[0].length : 0)
  const endMatch = FLD_CHAR_END_REGEX.exec(fieldSlice)
  const nextInstrMatch = fieldSlice.slice(1).search(new RegExp(`<${NS}instrText`, 'i'))
  const endIndex = endMatch?.index ?? (nextInstrMatch >= 0 ? nextInstrMatch + 1 : fieldSlice.length)
  return textFromFragment(fieldSlice.slice(0, endIndex)).trim()
}

/** Collect cross-reference links from OOXML (complex fields and fldSimple). */
export function extractCrossReferenceLinksFromOoxml(ooxml: string): CrossReferenceLink[] {
  if (!ooxml) return []

  const links: CrossReferenceLink[] = []

  for (const { instruction, afterIndex } of mergedInstrTextBlocks(ooxml)) {
    const crossReference = crossReferenceFromInstruction(instruction)
    if (!crossReference) continue
    const displayText = displayTextAfterInstruction(ooxml, afterIndex)
    if (displayText) links.push({ ...crossReference, displayText })
  }

  let fldMatch: RegExpExecArray | null
  FLD_SIMPLE_REGEX.lastIndex = 0
  while ((fldMatch = FLD_SIMPLE_REGEX.exec(ooxml)) !== null) {
    const instrMatch = fldMatch[1].match(/(?:\w+:)?instr="([^"]*)"/)
    if (!instrMatch) continue
    const crossReference = crossReferenceFromInstruction(decodeXmlText(instrMatch[1]))
    if (!crossReference) continue
    const displayText = textFromFragment(fldMatch[2]).trim()
    if (displayText) links.push({ ...crossReference, displayText })
  }

  return links
}
